import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import style from "./newOrderFilter.module.scss";
import CustomSelect from "../../custom/custom-select";
import CustomSelectMulti from "../../custom/custom-select/CustomSelectMulti";
import CustomButton from "../../custom/custom-button/CustomButton";
import FilterDatePicker from "../../custom/сustom-filter-date/FilterDatePicker";
import CustomInput from "../../custom/custom-input/CustomInput";
import { XIcon } from "lucide-react";
import { ORDER_STATUSES } from "../../../mock";
import FilterIcon from "../../shared/icons/FilterIcon";
import { Order, Expeditor } from "../../../services";
import { useLanguage } from "../../../context/LanguageContext";
import { TRANSPORTATION_TYPES } from "../../../pages/orders/utils/railwayOrder";

const OrderFilterExpeditor = ({
  isOpen,
  onClose,
  filterValues,
  filterSetters,
  filterMeta,
  statusActive,
  onSwitchToAll,
  transportationType,
}) => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();

  const {
    countryFrom,
    countryTo,
    cityFrom,
    cityTo,
    loadingDate,
    creationDate,
    selectedStatus,
    selectedCurrency,
    senderName,
    orderNo,
    receiverName,
    executor,
    expeditorInsuranceIncluded,
  } = filterValues;

  const {
    setCountryFrom,
    setCountryTo,
    setCityFrom,
    setCityTo,
    setLoadingDate,
    setCreationDate,
    setSelectedStatus,
    setSelectedCurrency,
    setSenderName,
    setOrderNo,
    setReceiverName,
    setExecutor,
    setExpeditorInsuranceIncluded,
  } = filterSetters;

  const { countries } = filterMeta;

  const [localCountryFrom, setLocalCountryFrom] = useState(countryFrom || "");
  const [localCountryTo, setLocalCountryTo] = useState(countryTo || "");
  const [localCitiesFrom, setLocalCitiesFrom] = useState([]);
  const [localCitiesTo, setLocalCitiesTo] = useState([]);

  const [localCityFrom, setLocalCityFrom] = useState(cityFrom || "");
  const [localCityTo, setLocalCityTo] = useState(cityTo || "");
  const [localStatus, setLocalStatus] = useState(Array.isArray(selectedStatus) ? selectedStatus : []);
  const [localCurrency, setLocalCurrency] = useState(selectedCurrency || "");
  const [localSenderName, setLocalSenderName] = useState(senderName || "");
  const [localOrderNo, setLocalOrderNo] = useState(orderNo || "");
  const [localReceiverName, setLocalReceiverName] = useState(receiverName || "");
  const [localExecutor, setLocalExecutor] = useState(executor || "");
  const [localInsuranceIncluded, setLocalInsuranceIncluded] = useState(expeditorInsuranceIncluded ?? null);
  const [partners, setPartners] = useState([]);
  const [loadingDates, setLoadingDates] = useState(loadingDate || []);
  const [creationDates, setCreationDates] = useState(creationDate || []);
  const [selectKey, setSelectKey] = useState(0);

  useEffect(() => {
    Expeditor.getPartners()
      .then((response) => {
        if (response.status === 200) {
          const filteredPartners = response.data
            .filter((partner) => partner.state === "Confirmed" && partner.companyName)
            .map((partner) => ({
              ...partner,
              name: partner.companyName,
            }));

          setPartners(filteredPartners);
        } else {
          console.log("Список пуст");
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  useEffect(() => {
    if (!localCountryFrom) {
      setLocalCitiesFrom([]);
      setLocalCityFrom("");
      return;
    }
    Order.getCities(currentLanguage, localCountryFrom.id)
      .then((res) => res.data)
      .then((data) => {
        const sorted = data?.sort((a, b) => a.name.localeCompare(b.name));
        setLocalCitiesFrom(sorted || []);
      })
      .catch((error) => console.error("Error getting cities (from):", error));
  }, [localCountryFrom, currentLanguage]);

  useEffect(() => {
    if (!localCountryTo) {
      setLocalCitiesTo([]);
      setLocalCityTo("");
      return;
    }
    Order.getCities(currentLanguage, localCountryTo.id)
      .then((res) => res.data)
      .then((data) => {
        const sorted = data?.sort((a, b) => a.name.localeCompare(b.name));
        setLocalCitiesTo(sorted || []);
      })
      .catch((error) => console.error("Error getting cities (to):", error));
  }, [localCountryTo, currentLanguage]);

  useEffect(() => {
    if (isOpen) {
      setLocalCountryFrom(countryFrom || "");
      setLocalCountryTo(countryTo || "");
      setLocalCityFrom(cityFrom || "");
      setLocalCityTo(cityTo || "");
      setLocalStatus(Array.isArray(selectedStatus) ? selectedStatus : []);
      setLocalCurrency(selectedCurrency || "");
      setLocalSenderName(senderName || "");
      setLocalOrderNo(orderNo || "");
      setLocalReceiverName(receiverName || "");
      setLocalExecutor(executor || "");
      setLocalInsuranceIncluded(expeditorInsuranceIncluded ?? null);
      setLoadingDates(loadingDate || []);
      setCreationDates(creationDate || []);
    }
  }, [isOpen]);

  const statusOptions = Object.entries(ORDER_STATUSES(t))
    .filter(([key]) => key !== "awaitingaction")
    .map(([key, value]) => ({
      id: key,
      value: key,
      name: value,
    }));

  const currencyOptions = [
    { id: "USD", value: "USD", name: "USD" },
    { id: "KZT", value: "KZT", name: "KZT" },
    { id: "EUR", value: "EUR", name: "EUR" },
  ];

  const isStatusDisabled = statusActive && statusActive.key !== "all";

  const insuranceOptions = [
    { id: "included", value: true, name: t("orderFilter.insuranceIncluded") },
    { id: "notIncluded", value: false, name: t("orderFilter.insuranceNotIncluded") },
  ];

  const handleApply = () => {
    setCountryFrom(localCountryFrom);
    setCountryTo(localCountryTo);
    setCityFrom(localCityFrom);
    setCityTo(localCityTo);
    setLoadingDate(loadingDates);
    setSelectedStatus(localStatus);
    setSelectedCurrency(localCurrency);
    setSenderName(localSenderName);
    setOrderNo(localOrderNo);
    setReceiverName(localReceiverName);
    setCreationDate(creationDates);
    setExecutor(localExecutor);
    setExpeditorInsuranceIncluded(localInsuranceIncluded);
    if (onSwitchToAll) onSwitchToAll();
    onClose();
  };

  const handleReset = () => {
    setLocalCountryFrom("");
    setLocalCountryTo("");
    setLocalCitiesFrom([]);
    setLocalCitiesTo([]);
    setLocalCityFrom("");
    setLocalCityTo("");
    setLocalStatus([]);
    setSelectKey((prev) => prev + 1);
    setLocalCurrency("");
    setLocalSenderName("");
    setLocalOrderNo("");
    setLocalReceiverName("");
    setLocalExecutor("");
    setLocalInsuranceIncluded(null);
    setLoadingDates([]);
    setCreationDates([]);

    // Сбрасываем глобальный стейт
    setCountryFrom("");
    setCountryTo("");
    setCityFrom("");
    setCityTo("");
    setLoadingDate("");
    setSelectedStatus([]);
    setSelectedCurrency("");
    setSenderName("");
    setOrderNo("");
    setReceiverName("");
    setCreationDate([]);
    setExecutor("");
    setExpeditorInsuranceIncluded(null);

    onClose();
  };

  return (
    <div className={cn(style.overlay, { [style.overlay_open]: isOpen })}>
      <div className={style.backdrop} onClick={onClose} />

      <div className={style.drawer}>
        <div className={style.header}>
          <div className={style.headerLeft}>
            <FilterIcon size={20} color="#212121" />
            <span className={style.title}>{t("orderFilter.title")}</span>
          </div>
          <button type="button" className={style.closeBtn} onClick={onClose}>
            <XIcon size={20} />
          </button>
        </div>

        <div className={style.body}>
          {/* Место */}
          <div>
            <div className={style.sectionTitle}>{t("orderFilter.placeSection")}</div>
            <div className={style.sectionBody}>
              {/* From: страна + город */}
              <div className={style.row}>
                <div className={style.field}>
                  <CustomSelect
                    key={"countries-from"}
                    labelText={t("orderFilter.from")}
                    placeholder={t("orderFilter.countryFromLabel")}
                    options={countries}
                    defaultValue={
                      countries.find((country) => country.id === localCountryFrom?.id) || localCountryFrom
                    }
                    onSubmit={(item) => {
                      setLocalCountryFrom(item);
                      setLocalCityFrom("");
                    }}
                    returnObject
                    usePortal
                  />
                </div>
                <div className={style.field}>
                  <CustomSelect
                    key={"cities-from"}
                    labelText={t("orderFilter.from")}
                    placeholder={t("orderFilter.loadingCityPlaceholder")}
                    options={localCitiesFrom}
                    defaultValue={localCityFrom}
                    onSubmit={setLocalCityFrom}
                    returnObject
                    usePortal
                  />
                </div>
              </div>

              {/* To: страна + город */}
              <div className={style.row}>
                <div className={style.field}>
                  <CustomSelect
                    key={"countries-to"}
                    labelText={t("orderFilter.to")}
                    placeholder={t("orderFilter.countryToLabel")}
                    options={countries}
                    defaultValue={
                      countries.find((country) => country.id === localCountryTo?.id) || localCountryTo
                    }
                    onSubmit={(item) => {
                      setLocalCountryTo(item);
                      setLocalCityTo("");
                    }}
                    returnObject
                    usePortal
                  />
                </div>
                <div className={style.field}>
                  <CustomSelect
                    key={"cities-to"}
                    labelText={t("orderFilter.to")}
                    placeholder={t("orderFilter.unloadingCityPlaceholder")}
                    options={localCitiesTo}
                    defaultValue={localCityTo}
                    onSubmit={setLocalCityTo}
                    returnObject
                    usePortal
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Дата */}
          <div>
            <div className={style.sectionTitle}>{t("orderFilter.dateSection")}</div>
            <div className={style.sectionBody}>
              <div className={style.row}>
                <div className={style.field}>
                  <FilterDatePicker
                    isRange
                    isEditable
                    noMarginBottom
                    isMobile
                    labelText={t("orderFilter.loadingDateLabel")}
                    placeholderText={t("orderFilter.datePlaceholder")}
                    date={loadingDates}
                    setDate={setLoadingDates}
                  />
                </div>
                <div className={style.field}>
                  <FilterDatePicker
                    isRange
                    isEditable
                    noMarginBottom
                    isMobile
                    labelText={t("orderFilter.creationDate")}
                    placeholderText={t("orderFilter.datePlaceholder")}
                    date={creationDates}
                    setDate={setCreationDates}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Параметры заказа */}
          <div>
            <div className={style.sectionTitle}>{t("orderFilter.orderParamsSection")}</div>
            <div className={style.sectionBody}>
              <div className={style.row}>
                <div className={style.field}>
                  <CustomInput
                    labelText={t("orderFilter.senderName")}
                    placeholder={t("orderFilter.senderName")}
                    value={localSenderName}
                    onChangeValue={setLocalSenderName}
                    isBgGray
                  />
                </div>
                <div className={style.field}>
                  <CustomInput
                    labelText={t("orderFilter.receiverName")}
                    placeholder={t("orderFilter.receiverName")}
                    value={localReceiverName}
                    onChangeValue={setLocalReceiverName}
                    isBgGray
                  />
                </div>
              </div>
              <div className={style.row}>
                <div className={style.field}>
                  <CustomInput
                    inputType="text"
                    labelText={t("orderFilter.orderNo")}
                    placeholder={t("orderFilter.orderNo")}
                    value={localOrderNo}
                    onChangeValue={setLocalOrderNo}
                    isBgGray
                  />
                </div>
                <div className={style.field}>
                  <CustomSelectMulti
                    key={selectKey}
                    labelText={t("orderFilter.statusLabel")}
                    placeholder={t("orderFilter.statusPlaceholder")}
                    options={statusOptions}
                    defaultValue={isStatusDisabled ? [] : localStatus}
                    isMulti
                    onSubmit={setLocalStatus}
                    usePortal
                    forceMenuTop
                  />
                </div>
              </div>
              {partners.length > 1 && (
                <div className={style.row}>
                  <div className={style.field}>
                    <CustomSelect
                      usePortal
                      menuPlacement="top"
                      labelText={"Партнер"}
                      placeholder={"Партнер"}
                      options={partners}
                      defaultValue={localExecutor}
                      returnObject
                      onSubmit={setLocalExecutor}
                    />
                  </div>
                </div>
              )}
              <div className={style.row}>
                <div className={style.field}>
                  <CustomSelect
                    usePortal
                    menuPlacement="top"
                    labelText={t("orderFilter.currency")}
                    placeholder={t("orderFilter.currencyPlaceholder")}
                    options={currencyOptions}
                    defaultValue={localCurrency}
                    returnObject
                    onSubmit={setLocalCurrency}
                  />
                </div>
                {transportationType === TRANSPORTATION_TYPES.ROAD && (
                  <div className={style.field}>
                    <CustomSelect
                      inputId="expeditor-insurance-filter"
                      labelText={t("orderFilter.insurance")}
                      options={insuranceOptions}
                      defaultValue={
                        insuranceOptions.find((option) => option.value === localInsuranceIncluded) ?? null
                      }
                      onSubmit={(option) => setLocalInsuranceIncluded(option.value)}
                      returnObject
                      usePortal
                      menuPlacement="top"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className={style.footer}>
          <CustomButton text={t("orderFilter.resetButton")} isUnderline onClick={handleReset} />
          <button type="button" className={style.applyBtn} onClick={handleApply}>
            {t("orderFilter.applyButton")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderFilterExpeditor;
